import type {
  GetFavoriteVehiclesQuery,
  InternalFileColumnsFragment,
  VehicleCfdSummary
} from '@graphql/generated';
import type { OrderGuideNote } from './order-guide-note.util';

/** A favorited vehicle as selected by `GET_FAVORITE_VEHICLES` — dates included. */
export type DashboardVehicle = GetFavoriteVehiclesQuery['favoriteVehicles'][number];

/** An internal-only order-guide attachment (cmt."File"), as shown on a dashboard tile/dialog. */
export type OrderGuideAttachment = InternalFileColumnsFragment;

/** Everything one pair of tiles needs, assembled once so the tiles stay presentational. */
export interface DashboardVehicleTile {
  vehicle: DashboardVehicle;
  activeDate: string | null;
  liveDate: string | null;
  /** Canada only: stored Translation date, or Active Date − 21 days when that row is absent. */
  translationDate: string | null;
  sorpDate: string | null;
  /** Internal-only order-guide notes for this vehicle (cmt."Note"). */
  notes: OrderGuideNote[];
  /** Internal-only order-guide attachments for this vehicle (cmt."File"). */
  attachments: OrderGuideAttachment[];
  cfd: VehicleCfdSummary | null;
}

/** Vehicles for one brand within a region, e.g. "Chevrolet Truck". */
export interface DashboardBrandGroup {
  divisionId: number;
  divisionName: string;
  tiles: DashboardVehicleTile[];
}

export interface DashboardRegionGroup {
  regionId: number;
  regionName: string;
  brands: DashboardBrandGroup[];
}

/** Top-level grouping, newest model year first. */
export interface DashboardYearGroup {
  modelYear: number;
  regions: DashboardRegionGroup[];
}

/** "none, or the first few characters" preview used for Notes and Attachments on the tile. */
export function previewText(value: string | null | undefined, max = 24): string {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) {
    return 'None';
  }
  return trimmed.length <= max ? trimmed : `${trimmed.slice(0, max - 1)}…`;
}
